//Common
import {
  CODIGO_GRUPO_CONTA_RECEITA,
  TIPO_SOLICITACAO,
} from "@/common/constantes";

import {
  fn_addDay,
  fn_dataIsBefore,
  fn_getDataInicioPosicaoDia,
  fn_getDataPrimeiroDiaMes,
  fn_getDataUltimoDiaMes,
} from "@/common/utils/date";

//Util
import {
  fn_getTiposSolicitacaoUsuario,
  fn_usuarioIsAdmin,
  fn_usuarioIsSystem,
} from "@/frontend/utils/sessao";

/*
 * Funções de apoio do dashboard (pages/dashboard).
 * Spec: documentos/specs/2026-10-24-dashboard-receitas.md
 */

//Opções do dashboard (pagamentos e receitas), na ordem em que aparecem na escolha
const OPCOES_TIPO_DASHBOARD = [
  { value: TIPO_SOLICITACAO.PAGAMENTO, label: "Pagamentos" },
  { value: TIPO_SOLICITACAO.RECEITA, label: "Receitas" },
];

/*
 * Retorna as opções do dashboard que o usuário pode ver (pagamentos e/ou receitas), conforme o perfil
 * (fn_getOpcoesTipoDashboardPerfil).
 */
export function fn_getOpcoesTipoDashboardUsuario(sessaoUsuario) {
  return fn_getOpcoesTipoDashboardPerfil(sessaoUsuario);
}

/*
 * Retorna as opções do dashboard que o usuário pode ver (pagamentos e/ou receitas), conforme o acesso do perfil
 * aos tipos de solicitação (acesso_tipos_solicitacao vazio = acesso aos dois tipos).
 * O usuário system e o admin veem os dois. Sem acesso a nenhum dos dois, vê o de pagamentos (como antes do dashboard de receitas).
 */
export function fn_getOpcoesTipoDashboardPerfil(sessaoUsuario) {
  if (fn_usuarioIsSystem(sessaoUsuario) || fn_usuarioIsAdmin(sessaoUsuario)) {
    return OPCOES_TIPO_DASHBOARD;
  }

  const tiposUsuario = fn_getTiposSolicitacaoUsuario(sessaoUsuario);

  if (tiposUsuario.length == 0) {
    return OPCOES_TIPO_DASHBOARD;
  }

  const opcoes = OPCOES_TIPO_DASHBOARD.filter((opcao) =>
    tiposUsuario.includes(opcao.value),
  );

  return opcoes.length > 0 ? opcoes : [OPCOES_TIPO_DASHBOARD[0]];
}

//Filtro do grupo da conta com os dois grupos marcados (padrão): os códigos dos dois grupos separados por vírgula
export const GRUPO_CONTA_DASHBOARD_RECEITA_AMBOS = `${CODIGO_GRUPO_CONTA_RECEITA.RECEITAS_FINANCEIRAS},${CODIGO_GRUPO_CONTA_RECEITA.OUTRAS_RECEITAS_OPERACIONAIS}`;

//Nome do checkbox do grupo da conta no filtro do dashboard de receitas (um checkbox para cada grupo)
export function fn_getNomeCampoGrupoContaDashboardReceita(codigoGrupo) {
  return `grupo_conta_${codigoGrupo}`;
}

/*
 * Retorna os códigos dos grupos da conta marcados no filtro do dashboard de receitas, separados por vírgula
 * (ex: "2,5" com os dois marcados), no formato do filtro codigo_grupo_conta. Nenhum marcado: "".
 * @body: dados do formulário (os checkboxes dos grupos, true/false)
 */
export function fn_getCodigoGrupoContaDashboardReceita(body) {
  return fn_loadOpcoesGrupoContaDashboardReceita()
    .filter(
      (opcao) => body?.[fn_getNomeCampoGrupoContaDashboardReceita(opcao.value)],
    )
    .map((opcao) => opcao.value)
    .join(",");
}

//Opções do filtro do grupo da conta no dashboard de receitas (checkboxes; o padrão é os dois marcados)
export function fn_loadOpcoesGrupoContaDashboardReceita() {
  return [
    {
      value: CODIGO_GRUPO_CONTA_RECEITA.RECEITAS_FINANCEIRAS,
      label: "Receitas financeiras",
      textTip: "Descontos em títulos",
    },
    {
      value: CODIGO_GRUPO_CONTA_RECEITA.OUTRAS_RECEITAS_OPERACIONAIS,
      label: "Outras receitas operacionais",
      textTip: "Depósito ambos",
    },
  ];
}

/*
 * Opções do radio do grupo da conta no dashboard de receitas: os dois grupos e, por último, "Ambos" (padrão).
 * O valor é o do filtro codigo_grupo_conta (um código ou, em Ambos, os dois separados por vírgula)
 */
export function fn_loadOpcoesRadioGrupoContaDashboardReceita() {
  return [
    ...fn_loadOpcoesGrupoContaDashboardReceita().map((opcao) => ({
      ...opcao,
      espaco: true,
    })),
    { value: GRUPO_CONTA_DASHBOARD_RECEITA_AMBOS, label: "Ambos" },
  ];
}

//Indica se o grupo da conta do filtro do dashboard de receitas inclui o grupo informado (com os dois marcados, inclui os dois)
export function fn_isGrupoContaDashboardReceita(
  codigoGrupoContaFiltro,
  codigoGrupo,
) {
  return String(codigoGrupoContaFiltro ?? "")
    .split(",")
    .some((codigo) => codigo == codigoGrupo);
}

/*
 * Retorna o período do vencimento (DD/MM/YYYY) do mês/ano inicial ao final: { data_inicio, data_fim }
 */
export function fn_getPeriodoMesAnoVencimento(mesAnoInicio, mesAnoFim) {
  return {
    data_inicio: fn_getDataPrimeiroDiaMes(mesAnoInicio),
    data_fim: fn_getDataUltimoDiaMes(mesAnoFim),
  };
}

/*
 * Retorna o período do vencimento da opção "Qtde dias antes" do dashboard de pagamentos: { data_inicio, data_fim },
 * da data atual até a data atual mais a quantidade de dias (dias corridos), ou null se a quantidade não for
 * um número inteiro maior do que zero.
 * @dataAtual: DD/MM/YYYY. @qtdeDias: quantidade de dias antes informada na tela
 */
export function fn_getPeriodoQtdeDiasAntes(dataAtual, qtdeDias) {
  const qtde = Number(qtdeDias);

  if (String(qtdeDias ?? "").trim() == "" || !Number.isInteger(qtde) || qtde <= 0) {
    return null;
  }

  return { data_inicio: dataAtual, data_fim: fn_addDay(dataAtual, qtde) };
}

/*
 * Retorna o último vencimento atrasado (DD/MM/YYYY): o dia anterior ao início da posição do dia.
 * Utilizado nos cards Atrasados do período e Atrasados total (dashboard de receitas).
 * @dataAtual: DD/MM/YYYY. @datasFeriados: datas DD/MM/YYYY dos feriados que afetam pagamentos
 */
export function fn_getDataUltimoVencimentoAtrasado(
  dataAtual,
  datasFeriados = [],
) {
  return fn_addDay(fn_getDataInicioPosicaoDia(dataAtual, datasFeriados), -1);
}

/*
 * Retorna o período do vencimento dos pagamentos atrasados do card Atrasados do período (dashboard de receitas): { data_inicio, data_fim },
 * ou null se no período ainda não tiver vencimento atrasado.
 * Está atrasado o vencimento anterior ao início da posição do dia: o vencimento no sábado, domingo ou feriado
 * que afeta pagamentos passa para o próximo dia útil. Ex: na segunda, o vencimento do sábado e do domingo ainda não está atrasado.
 * @dataAtual: DD/MM/YYYY. @datasFeriados: datas DD/MM/YYYY dos feriados que afetam pagamentos
 */
export function fn_getPeriodoAtrasadosReceita(
  mesAnoInicio,
  mesAnoFim,
  dataAtual,
  datasFeriados = [],
) {
  const periodo = fn_getPeriodoMesAnoVencimento(mesAnoInicio, mesAnoFim);

  const ultimoAtrasado = fn_getDataUltimoVencimentoAtrasado(
    dataAtual,
    datasFeriados,
  );

  const dataFim = fn_dataIsBefore(ultimoAtrasado, periodo.data_fim)
    ? ultimoAtrasado
    : periodo.data_fim;

  if (fn_dataIsBefore(dataFim, periodo.data_inicio)) {
    return null;
  }

  return { data_inicio: periodo.data_inicio, data_fim: dataFim };
}
